import React, { useState, useEffect, useRef } from "react";
import { FaThumbtack } from "react-icons/fa";
import axios from "axios";
import StepChart from "../../../Meeting/CreateNewMeeting/StepChart";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL, Assets_URL } from "../../../../Apicongfig";
import MessageTypeWritter from "../../../Discussion/MessageTypeWritter";
import { updateMessage, deleteMessage, createMessage, getMeetingMessages } from "../../../Discussion/api";

function MeetingDiscussion({ meetingId, messages, selectedMoment, onMessagesUpdate }) {
  //   const [messages, setMessages] = useState([]);
  //   const [selectedMoment, setSelectedMoment] = useState(null);
  const [loading, setLoading] = useState({ messages: true });
  const [editingMessage, setEditingMessage] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedMessageForAction, setSelectedMessageForAction] =
    useState(null);
  const [isDrop, setIsDrop] = useState(false);
  const [id, setId] = useState(null);
  const [actionModalId, setActionModalId] = useState(null);
  const [t] = useTranslation("global");
  const editorRef = useRef(null);
  const navigate = useNavigate();
  const [attachments, setAttachments] = useState([]);

  const messagesEndRef = useRef(null);
  const messageContainerRef = useRef(null);
  const scrollToBottom = () => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [meetingId]);

  const handleHideAction = (shouldRefresh) => {
    setShowActionModal(false);
    if (shouldRefresh && meetingId && selectedMoment) {
      const fetchMessages = async () => {
        try {
          const messagesData = await getMeetingMessages(meetingId, selectedMoment.id);
          const sortedMessages = [...(messagesData?.data || [])].sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at)
          );
          onMessagesUpdate(sortedMessages);
        } catch (error) {
          console.error("Error fetching messages:", error);
        }
      };
      fetchMessages();
    }
  };

  const handleSendMessage = async (messageContent) => {
    try {
      const formData = new FormData();
      const userId = parseInt(sessionStorage.getItem("user_id"));
  
      if (editingMessage) {
        // Edit existing message
        formData.append("_method", "PUT");
        formData.append("message", messageContent.message);
  
        const response = await fetch(`${API_BASE_URL}/meeting-messages/${editingMessage.id}`, {
          method: 'POST', // Using POST with _method=PUT for Laravel
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        });
  
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to update message');
        }
  
        const responseData = await response.json();
  
        if (responseData?.success) {
          onMessagesUpdate?.(prevMessages => 
            prevMessages.map(msg => 
              msg.id === editingMessage.id ? responseData.data : msg
            )
          );
          setEditingMessage?.(null);
          setAttachments?.([]);
        }
  
      } else {
        // Create new message
        formData.append("meeting_id", selectedMoment?.id);
        if(userId){
          formData.append("user_id", userId);
        }
        formData.append("message", messageContent.message);
  
        // Add attachments
        attachments?.forEach((file, index) => {
          formData.append(`attachments[${index}]`, file);
        });
  
        const response = await fetch(`${API_BASE_URL}/meeting-messages`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        });
  
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to send message');
        }
  
        const responseData = await response.json();
  
        if (responseData?.success) {
          // Functional update to ensure we have latest state
          onMessagesUpdate?.(prevMessages => [...prevMessages, responseData.data]);
          setAttachments?.([]);
          
          // Scroll to bottom in next tick after DOM updates
          requestAnimationFrame(() => {
            const messageContainer = document.querySelector(".message-container");
            messageContainer?.scrollTo({
              top: messageContainer.scrollHeight,
              behavior: 'smooth'
            });
          });
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      // toast.error(error.message || "Failed to send message");
      // Optionally revert optimistic updates here
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditMessage = (message) => {
    setEditingMessage(message);
    if (editorRef.current) {
      editorRef.current.setContent(message.message);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (window.confirm("Are you sure you want to delete this message?")) {
      try {
        const response = await deleteMessage(messageId);
        if (response.success) {
          // Use the callback to update messages in parent component
          onMessagesUpdate(messages.filter(msg => msg.id !== messageId));
        }
      } catch (error) {
        console.error("Error deleting message:", error);
      }
    }
  };
  const handlePinMessage = async (messageId, isPinned) => {
    try {
        const formData = new FormData();
        formData.append("is_pinned", !isPinned);
        formData.append("_method", "PUT");

        const response = await fetch(`${API_BASE_URL}/meeting-messages/${messageId}`, {
            method: 'POST',  // POST with _method=PUT for Laravel
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                // Let browser set Content-Type with boundary
            },
            body: formData,
            signal: AbortSignal.timeout(10000) // 10 second timeout
        });

        // Check for HTTP errors
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
                errorData.message || 
                `Failed to update pin status (HTTP ${response.status})`
            );
        }

        const responseData = await response.json();

        // Verify API response structure
        if (!responseData || !responseData.success) {
            throw new Error('Invalid response format from server');
        }

        // Update parent component's state
        if (onMessagesUpdate) {
            onMessagesUpdate(currentMessages => 
                currentMessages.map(msg => 
                    msg.id === messageId ? { 
                        ...msg, 
                        is_pinned: !isPinned,
                        ...responseData.data // Merge any additional updated fields
                    } : msg
                )
            );
        }

        // Optional success feedback
        // toast.success(`Message ${!isPinned ? 'pinned' : 'unpinned'} successfully`);

    } catch (error) {
        console.error("Error pinning message:", error);
        
        // User-friendly error handling
        // toast.error(error.message || "Failed to update pin status");
        
        // Optionally revert optimistic update here if needed
    }
};

  //   const handleMomentSelect = async (moment) => {
  //     setSelectedMoment(moment);
  //     setEditingMessage(null);

  //     try {
  //       const readResponse = await axios.post(
  //         `${API_BASE_URL}/meeting/${moment?.id}/message-read`,
  //         null,
  //         {
  //           headers: {
  //             Authorization: `Bearer ${sessionStorage.getItem("token")}`,
  //             "Content-Type": "application/json",
  //           },
  //         }
  //       );

  //       if (readResponse.status === 200 || readResponse.status === 201) {
  //         const meetingsResponse = await getDestinationMeetings(
  //           moment.destination_id
  //         );

  //         if (meetingsResponse?.data) {
  //           onMeetingsUpdate(meetingsResponse.data);
  //         }
  //       }
  //     } catch (error) {
  //       console.error("Error in moment selection:", error);
  //     }
  //   };

  const cancelEdit = () => {
    setEditingMessage(null);
    if (editorRef.current) {
      editorRef.current.setContent("");
    }
  };
 const handleShowAction = (message) => {
    console.log("message", message);
    if(message?.message_type === "notification"){
      navigate(`${message?.redirect_url}`)
    }else{
      setSelectedMessageForAction(message);
      setActionModalId(message);
      setShowActionModal(true);
    }
  };



  const MessageItem = ({
    message,
    onEdit,
    onDelete,
    onPin,
    onShowAction,
    isEditing,
    currentUserId,
  }) => {
    const isCurrentUser = message.user_id === currentUserId;

    const formatDateForDisplay = (isoString) => {
      return new Date(isoString).toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    };

       const renderAttachment = (attachment) => {
          const fileType = attachment?.type?.split('/')[0];
          const fileUrl = `${Assets_URL}/${attachment?.path}`;
          
          switch(fileType) {
            case 'image':
              return (
                <div className="attachment-item mb-2">
                  <img 
                    src={fileUrl} 
                    alt="Attachment" 
                    className="img-fluid rounded"
                    style={{ maxHeight: '200px' }}
                  />
                </div>
              );
            case 'application':
              return (
                <div className="attachment-item mb-2">
                  <a 
                    href={fileUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="d-flex align-items-center gap-2 text-decoration-none"
                  >
                    <div className="bg-light p-2 rounded">
                      <i className="bi bi-file-earmark-text fs-4"></i>
                    </div>
                    <div>
                      <div className="text-primary">{attachment.name}</div>
                      <small className="text-muted">{(attachment.size / 1024).toFixed(2)} KB</small>
                    </div>
                  </a>
                </div>
              );
            default:
              return (
                <div className="attachment-item mb-2">
                  <a 
                    href={fileUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="d-flex align-items-center gap-2 text-decoration-none"
                  >
                    <div className="bg-light p-2 rounded">
                      <i className="bi bi-file-earmark fs-4"></i>
                    </div>
                    <div>
                      <div className="text-primary">{attachment.name}</div>
                      <small className="text-muted">{(attachment.size / 1024).toFixed(2)} KB</small>
                    </div>
                  </a>
                </div>
              );
          }
        };
    return (
      <div
        className={`message mb-4 ${message?.is_pinned ? "pinned-message" : ""}`}
      >
        {message?.is_pinned && (
          <div className="pinned-badge text-muted mb-1">
            <FaThumbtack className="me-1" /> {t("Pinned")}
          </div>
        )}
        <div className="d-flex gap-3">
          <div className="flex-shrink-0">
            <img
              src={
                message?.user?.image ? (message.user?.image?.startsWith("http")
                  ? message?.user?.image
                  : Assets_URL + "/" + message?.user?.image) : message?.user_image
              }
              width={48}
              height={48}
              alt="User"
              className="rounded-circle object-fit-cover"
              style={{ objectPosition: "top" }}
            />
          </div>
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 mb-1">
              <strong className="text-primary">
                {message?.user?.full_name || message?.user_name}
              </strong>
              <small className="text-muted">
                {formatDateForDisplay(message.created_at)}
              </small>
            </div>
            <div
              className={`paragraph-parent message-content bg-light p-3 rounded-3 ${isEditing ? "editing" : ""
                }`}
              style={{
                overflow: "hidden",
              }}
            >
              <span className="paragraph paragraph-images">
                <div
                  dangerouslySetInnerHTML={{ __html: message.message }}
                  style={{
                    maxWidth: "100%",
                    wordBreak: "break-word",
                    "& img": {
                      maxWidth: "100%",
                      height: "auto",
                      display: "block",
                      margin: "5px 0",
                    },
                  }}
                />
              </span>
                {/* Display attachments */}
                {message.attachments && message.attachments.length > 0 && (
                <div className="attachments-container mt-3">
                  {message.attachments.map((attachment, index) => (
                    <div key={index}>
                      {renderAttachment(attachment)}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="d-flex gap-2 mt-2">
              {isCurrentUser && (
                <>
                  <button
                    className="btn btn-sm btn-link p-0 text-muted"
                    onClick={() => onEdit(message)}
                    disabled={isEditing}
                  >
                    {t("Edit")}
                  </button>
                  <span className="text-muted">•</span>
                  <button
                    className="btn btn-sm btn-link p-0 text-muted"
                    onClick={() => onDelete(message.id)}
                    disabled={isEditing}
                  >
                    {t("Delete")}
                  </button>
                  <span className="text-muted">•</span>
                </>
              )}
              <button
                className="btn btn-sm btn-link p-0 text-muted"
                onClick={() => onPin(message.id, message.is_pinned)}
              >
                {message.is_pinned ? t("Unpin") : t("Pin")}
              </button>
              <span className="text-muted">•</span>
           {sessionStorage.getItem('token') && 
  (message?.message_type === "notification" && message?.redirect_url === null ? null : (
    <button
      className="btn btn-sm btn-link p-0 text-muted"
      onClick={() => onShowAction(message)}
    >
      {message?.message_type === "notification" ? t("View Action") : t("Create Action")}
    </button>
  ))
}
            </div>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div className="mt-5">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="row justify-content-between">

              <div className="col-md-12 mt-3 px-3 px-md-2">
                <div className="card rounded-0 h-100">
                  <div
                    className="card-body d-flex flex-column"
                    style={{ minHeight: "70vh" }}
                  >
                    {/* {selectedMoment && (
                      <div className="moment-header mb-3 border-bottom pb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="mb-1">{selectedMoment.title}</h5>
                        
                        </div>
                      </div>
                    )} */}

                    <div
                      className="message-container flex-grow-1 mb-3"
                      ref={messageContainerRef}

                      style={{
                        overflowY: "auto",
                        maxHeight: "calc(70vh - 200px)",
                        scrollBehavior: "smooth",
                      }}
                    >
                      {messages?.length > 0 ? (
                        <>
                          {messages
                            .filter((msg) => msg.is_pinned)
                            .map((message) => (
                              <MessageItem
                                key={message.id}
                                message={message}
                                onEdit={handleEditMessage}
                                onDelete={handleDeleteMessage}
                                onPin={handlePinMessage}
                                onShowAction={handleShowAction}
                                isEditing={editingMessage?.id === message.id}
                                currentUserId={parseInt(
                                  sessionStorage.getItem("user_id")
                                )}
                              />
                            ))}
                          {messages
                            .filter((msg) => !msg.is_pinned)
                            .map((message) => (
                              <MessageItem
                                key={message.id}
                                message={message}
                                onEdit={handleEditMessage}
                                onDelete={handleDeleteMessage}
                                onPin={handlePinMessage}
                                onShowAction={handleShowAction}
                                isEditing={editingMessage?.id === message.id}
                                currentUserId={parseInt(
                                  sessionStorage.getItem("user_id")
                                )}
                              />
                            ))}
                        </>
                      ) : (
                        <div className="d-flex justify-content-center align-items-center h-100">
                          <div className="text-center text-muted py-4">
                            <i className="bi bi-chat-square-text fs-1"></i>
                            <p className="mt-2">
                              {selectedMoment
                                ? t("No messages yet for this moment")
                                : t("Select a moment to view messages")}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {selectedMoment && (
                      <div className="mt-auto">
                        <MessageTypeWritter
                          onSendMessage={handleSendMessage}
                          //   disabled={messages}
                          editorRef={editorRef}
                          isEditing={!!editingMessage}
                          onCancelEdit={cancelEdit}
                          participants={selectedMoment?.user_with_participants}
                          meeting={selectedMoment}
                          onFileUpload={handleFileUpload}
                          onRemoveAttachment={removeAttachment}
                          attachments={attachments}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>


            </div>
          </div>
        </div>
      </div>

      {showActionModal && (
        <div className="new-meeting-modal">
          <StepChart
            meetingId={selectedMoment?.id}
            id={id}
            show={showActionModal}
            setId={setId}
            closeModal={handleHideAction}
            meeting={selectedMoment}
            isDrop={isDrop}
            setIsDrop={setIsDrop}
            editorContent={actionModalId}
            openedFrom="discussion"
          />
        </div>
      )}
    </div>
  );
}

export default MeetingDiscussion;
